<template>
  <div ref="singleTask" class="single-task">
    <div class="title">
      {{ task.title }}
      <LmTooltip v-if="task.title.length > 200">
        {{ task.title }}
      </LmTooltip>
    </div>

    <div class="info">
      <div class="date-details">
        <span class="q-mr-sm">{{ taskHour }}</span>
        <span>{{ taskDate }}</span>
      </div>
      <span v-if="task.priority" :style="{ background: priorityColor(task.priority.name) }">{{
        task.priority.name
      }}</span>
    </div>

    <div v-if="hasDetails || hasOwners" class="related">
      <div class="assignees">
        <div
          v-for="assignee of task.owners.slice(0, 4)"
          :key="assignee.id"
          :style="{ background: assignee.color }"
          class="assignee"
        >
          {{
            assignee.name
              .split(' ')[0]
              .slice(0, 1)
              .toUpperCase()
          }}
          <template v-if="assignee.name.split(' ').length > 1">
            {{
              assignee.name
                .split(' ')[1]
                .slice(0, 1)
                .toUpperCase()
            }}
          </template>
          <LmTooltip> {{ assignee.name }} </LmTooltip>
        </div>

        <div v-if="task.owners.length > 4" class="assignee more">
          <span>...</span>
        </div>
      </div>

      <div class="details">
        <div v-if="task.checklist_items_count" class="icon-info">
          <CheckListSvg />
          <span>{{ task.checklist_items_count }}</span>
        </div>

        <div v-if="task.media_count" class="icon-info">
          <AttachmentSvg />
          <span>{{ task.media_count }}</span>
        </div>

        <div v-if="task.comments_count" class="icon-info">
          <CommentSvg />
          <span>{{ task.comments_count }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
// Components
import AttachmentSvg from '@/reusable/svg-icons/AttachmentSvg'
import CheckListSvg from '@/reusable/svg-icons/CheckListSvg'
import CommentSvg from '@/reusable/svg-icons/CommentSvg'

export default {
  components: {
    AttachmentSvg,
    CheckListSvg,
    CommentSvg
  },

  props: {
    task: Object
  },

  computed: {
    taskDate() {
      const date = this.task.created_at.split(' ')[0]
      return moment(date, 'YYYY/MM/DD').format('DD MMM')
    },

    taskHour() {
      const hour = this.task.created_at.split(' ')[1]
      return moment(hour, 'HH:mm:ss').format('HH:mm')
    },

    hasDetails() {
      const { checklist_items_count, media_count, comments_count } = this.task
      return checklist_items_count || media_count || comments_count
    },

    hasOwners() {
      return this.task.owners.length
    }
  },

  methods: {
    addAnimationsOnce() {
      // access from parent
      const assignees = this.$el.querySelectorAll('.assignee')

      assignees.forEach(el => {
        el.classList.add('animated')
        el.addEventListener('animationend', () => {
          el.classList.remove('animated')
        })
      })
    },

    priorityColor(color) {
      const colors = {
        low: '#65b5a5',
        medium: '#4f829e',
        high: '#1b4a6c'
      }
      return colors[color]
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/custom-variables'

.single-task {
  border-radius 3px
  background white
  display grid
  grid-template-rows auto 40px auto
  align-items start
  cursor pointer

  > .title {
    line-height 0.9rem
    max-height 45px
    color #050505
    font-size 1rem
    display -webkit-box
    -webkit-box-orient vertical
    -webkit-line-clamp 2
    overflow hidden
    padding 15px
    word-break break-word
  }

  > .info {
    display grid
    grid-auto-flow column
    justify-content space-between
    align-items center
    padding 8px 15px

    .date-details {
      color #535b7a
      font-size 0.875rem
      opacity 0.64
    }

    > span {
      padding 0 5px
      color white
      font-size 0.625rem
      font-weight 500
      text-transform capitalize
    }
  }

  > .related {
    display grid
    grid-template-columns 1fr auto
    align-items center
    height 100%
    border-top 1px solid $lm-grey
    padding 8px 12px

    > .assignees {
      display grid
      grid-template-columns repeat(auto-fit, 25px)

      .assignee {
        border-radius 50%
        width 31px
        height 31px
        font-size 0.75rem
        display flex
        justify-content center
        align-items center
        border 2px solid white
        color white
        font-weight 500
        cursor pointer

        &.animated {
          animation fadeInScaleIn 350ms ease-in-out both;

          for num in (1..5) {
            &:nth-child({num}) {
              animation-delay: (100ms * num) + 0.2s;
            }
          }
        }

        &.more > span {
          font-size 1.5rem
          height 100%
          line-height 20px
        }

      }
    }

    > .details {
      display grid
      grid-auto-flow column
      gap 10px

      > .icon-info {
        display grid
        grid-auto-flow column
        align-items center
        gap 5px

        > span {
          color #050505
          font-size 1rem
          font-weight 500
        }
      }
    }
  }
}

@keyframes fadeInScaleIn {
  from {
    opacity 0;
    transform scale(0.6) translateY(-8px)
  }

  to {
   opacity 1;
  }
}
</style>
